// src/services/quizService.ts
import { db, appId } from '../firebase';
import { collection, doc, addDoc, arrayRemove, deleteDoc, getDoc, getDocs, query, where, updateDoc, serverTimestamp, or, and, limit } from 'firebase/firestore';
import { Quiz } from '../types';
import { logger } from '../utils/logger';
// MOD: IndexedDBヘルパーをインポート
import { getQuizFromDb, setQuizToDb, deleteQuizFromDb } from '../lib/quizIdbHelper';


/**
 * 公開情報のクイズのみを取得する
 * キャッシュ（IndexedDB）またはFirestoreからクイズを取得します。
 * 公開クイズでない場合はキャッシュしません。
 * @param quizId - 対象のクイズID
 * @returns クイズデータ or null
 */
async function getPublicQuiz(quizId: string): Promise<Quiz | null> {
    // 1. IndexedDBから取得
    const cached = await getQuizFromDb(quizId);
    if (cached) {
        logger.log(`[getPublicQuiz] Quiz ${quizId} found in IndexedDB.`);
        return cached;
    }

    // 2. なければFirestoreから取得
    logger.log(`[getPublicQuiz] Fetching quiz ${quizId} from Firestore.`);
    const quizRef = doc(db, `artifacts/${appId}/public/data/quizzes`, quizId);
    const quizSnap = await getDoc(quizRef);
    
    if (!quizSnap.exists()) {
        return null;
    }

    const quiz = { id: quizSnap.id, ...quizSnap.data() } as Quiz;

    //公開クイズでない場合はnullを返す
    if (!quiz.isPublic) {
        return null;
    }

    // 3. 取得したデータをIndexedDBに保存
    await setQuizToDb(quiz);
    
    return quiz;
}


/**********************
 * 外部公開関数
 ***********************/
/**
 * 　キャッシュ（IndexedDB）またはFirestoreからクイズを取得します。
 * @param quizId - 対象のクイズID
 * @returns クイズデータ or null
 */
export async function fetchQuiz(quizId: string): Promise<Quiz | null> {
    // 1. IndexedDBから取得
    const cached = await getQuizFromDb(quizId);
    if (cached) {
        logger.log(`[fetchQuiz] Quiz ${quizId} found in IndexedDB.`);
        return cached;
    }

    // 2. なければFirestoreから取得
    logger.log(`[fetchQuiz] Fetching quiz ${quizId} from Firestore.`);
    const quizRef = doc(db, `artifacts/${appId}/public/data/quizzes`, quizId);
    const quizSnap = await getDoc(quizRef);
    
    if (!quizSnap.exists()) {
        return null;
    }

    const quiz = { id: quizSnap.id, ...quizSnap.data() } as Quiz;
    
    // 3. 取得したデータをIndexedDBに保存
    await setQuizToDb(quiz);
    
    return quiz;
}


/**
 * Firestoreから特定のクイズを取得する関数（キャッシュは利用しない）
 * @param quizId - 取得するクイズのID
 * @returns クイズデータ、またはnull
 */
export const fetchQuizById = async (quizId: string): Promise<Quiz | null> => {
    if (!quizId) return null;
    const quizRef = doc(db, `artifacts/${appId}/public/data/quizzes`, quizId);
    const snapshot = await getDoc(quizRef);
    return snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as Quiz) : null;
};

/**
 * クイズをFirestoreに追加する関数
 * 追加されたクイズのIDと完全なデータを含んだQuizオブジェクトを返します。
 * @param quizData - 追加するクイズのデータ（id, createdAt, updatedAtを除く）
 * @returns 追加されたクイズの完全なデータを持つQuizオブジェクト
 */
export const addQuiz = async (quizData: Omit<Quiz, 'id' | 'createdAt' | 'updatedAt'>): Promise<Quiz> => {
    logger.info("add QuizData:", quizData);

    const quizzesRef = collection(db, `artifacts/${appId}/public/data/quizzes`);

    const docRef = await addDoc(quizzesRef, {
        ...quizData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

    const newQuizSnap = await getDoc(docRef);
    if (newQuizSnap.exists()) {
        const fullQuizData = newQuizSnap.data() as Omit<Quiz, 'id'>;
        logger.log(`Quiz added with ID: ${newQuizSnap.id}`);
        const newQuiz = { id: newQuizSnap.id, ...fullQuizData } as Quiz;
        
        // MOD: 新規追加したクイズもキャッシュに保存
        await setQuizToDb(newQuiz);

        return newQuiz;
    } else {
        logger.error(`Failed to retrieve newly added quiz with ID: ${docRef.id}`);
        throw new Error("新しいクイズデータの取得に失敗しました。");
    }
};
/**
 * クイズをFirestoreで更新する関数
 * @param quizId - 更新するクイズのID
 * @param quizData - 更新するクイズのデータ（部分的な更新も可）
 */
export const updateQuiz = async (quizId: string, quizData: Partial<Quiz>): Promise<void> => {

    logger.info("update QuizData:", quizData);

    const quizRef = doc(db, `artifacts/${appId}/public/data/quizzes`, quizId);
    await updateDoc(quizRef, {
        ...quizData,
        updatedAt: serverTimestamp(),
    });

    // MOD: 更新後、キャッシュも無効化して次回最新データを取得させる
    await deleteQuizFromDb(quizId);
};

/**
 * クイズをFirestoreとキャッシュから削除する関数
 * @param quizId - 削除するクイズのID
 */
export const deleteQuiz = async (quizId: string): Promise<void> => {
    // MOD: IndexedDBからも削除
    await deleteQuizFromDb(quizId);
    const quizRef = doc(db, `artifacts/${appId}/public/data/quizzes`, quizId);
    await deleteDoc(quizRef);
};


/**
 * Firestoreから検索クエリに基づいてクイズを検索する関数
 * @param searchQuery - 検索クエリ
 * @param limitCount - 取得するクイズの最大数（デフォルト: 10）
 * @returns 検索結果のクイズ配列
 */
export const searchQuizzesInFirebase = async (searchQuery: string, limitCount: number = 10): Promise<Quiz[]> => {
    if (!searchQuery) return [];

    const quizzesRef = collection(db, `artifacts/${appId}/public/data/quizzes`);
    const actualLimit = Math.min(limitCount, 50);

    // searchQueryが "ID:XXXXX" の形式で始まる場合
    if (searchQuery.startsWith('ID_')) {
        const quizId = searchQuery.replace(/^ID_/, ''); // "ID:"を除去
        const quiz = await getPublicQuiz(quizId); //公開クイズを検索（公開クイズの場合はついでにキャッシュする）
        if (quiz && quiz.isPublic) { // isPublicがtrueの場合のみ返す
            return [quiz];
        }
        return []; // クイズが見つからない、または非公開の場合は空配列
    }

    // ID検索でない場合、元の検索ロジックを実行
    const q = query(
        quizzesRef,
        and(
            where('isPublic', '==', true),
            or(
                where('authorId', '==', searchQuery),
                where('authorNickname', '==', searchQuery),
                where('category', 'array-contains', searchQuery)
            )
        ),
        limit(actualLimit)
    );

    const querySnapshot = await getDocs(q);
    const quizzes: Quiz[] = [];

   // 取得した各クイズをループ処理して配列に追加し、キャッシュに保存
    querySnapshot.forEach(async (doc) => {
        const quiz = { id: doc.id, ...doc.data() } as Quiz;
        quizzes.push(quiz);
        await setQuizToDb(quiz);
    });
    return quizzes;
};

